// CSV: the money table out (the server writes it: UTF-8 with a BOM, for
// spreadsheets) and in (read here, csv.ts; sent in batches of 500 lines,
// each with a client_id from the file, so the same file twice adds nothing).
import { useState } from 'react'

import { errorText } from '../../api/client'
import { ErrorText, Field } from '../../ui/common'
import { tapi } from '../api'
import { type ImportPlan, batches, decodeCsv, planImport } from '../csv'
import { engine } from '../engine'
import { dayLabel, won } from '../format'
import type { Current } from './common'

const PREVIEW = 20

export function CsvScreen({ cur }: { cur: Current }) {
  const [file, setFile] = useState<File | null>(null)
  const [plan, setPlan] = useState<ImportPlan | null>(null)
  const [readError, setReadError] = useState<string | null>(null)
  const [progress, setProgress] = useState<{ sent: number; created: number } | null>(null)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function choose(f: File | undefined) {
    setPlan(null)
    setResult(null)
    setError(null)
    setReadError(null)
    setProgress(null)
    setFile(f ?? null)
    if (!f) return
    try {
      const text = decodeCsv(new Uint8Array(await f.arrayBuffer()))
      const p = planImport(text, f.name, f.size)
      if (p.error) setReadError(p.error)
      else setPlan(p)
    } catch {
      setReadError('파일을 읽지 못했습니다.')
    }
  }

  async function send() {
    if (!plan || !file || busy) return
    const rows = plan.lines.flatMap((l) => (l.row ? [l.row] : []))
    setBusy(true)
    setError(null)
    let sent = 0
    let created = 0
    setProgress({ sent, created })
    try {
      for (const b of batches(rows, 500)) {
        created += await engine.addRows(cur.workbook.id, b)
        sent += b.length
        setProgress({ sent, created })
      }
      const same = sent - created
      setResult(
        `${created}줄을 새로 더했습니다.` + (same > 0 ? ` ${same}줄은 이미 있던 것이라 더하지 않았습니다.` : ''),
      )
      setPlan(null)
    } catch (e) {
      setError(
        `${errorText(e)} (${sent}줄까지 보냈습니다. 같은 파일을 다시 가져오면 보낸 줄은 다시 더해지지 않습니다.)`,
      )
    } finally {
      setBusy(false)
    }
  }

  const bad = plan?.lines.filter((l) => l.error) ?? []
  const good = plan?.lines.filter((l) => l.row) ?? []

  return (
    <div className="page t-page">
      <section className="card">
        <h3>CSV로 내보내기</h3>
        <p className="muted small">거래 표 전체를 엑셀에서 열 수 있는 CSV 파일로 받습니다.</p>
        <a className="secondary" href={tapi.csvUrl(cur.workbook.id)} download>
          CSV 파일 받기
        </a>
      </section>
      <section className="card">
        <h3>CSV 가져오기</h3>
        <p className="muted small">
          첫 줄은 머리글입니다: 날짜, 구분(지출·수입), 금액, 사람, 분류(식비&gt;장보기), 결제수단, 메모. 처음 보는 이름은
          항목으로 더해집니다. 같은 파일을 두 번 가져와도 한 번만 들어갑니다.
        </p>
        <Field label="CSV 파일">
          <input
            type="file"
            accept=".csv,text/csv"
            disabled={busy}
            onChange={(e) => {
              void choose(e.target.files?.[0])
              e.target.value = '' // the same file again is a new choice
            }}
          />
        </Field>
        <ErrorText text={readError} />
        {plan && (
          <>
            <p role="status">
              {good.length}줄을 읽었습니다.{bad.length > 0 && ` 읽지 못한 줄이 ${bad.length}개 있습니다.`}
            </p>
            {bad.length > 0 && (
              <ul className="t-csv-errors small">
                {bad.slice(0, 50).map((l) => (
                  <li key={l.line} className="danger">
                    {l.line}번째 줄: {l.error}
                  </li>
                ))}
              </ul>
            )}
            {good.length > 0 && (
              <div className="t-scroll">
                <table className="t-table small">
                  <thead>
                    <tr>
                      <th>줄</th>
                      <th>날짜</th>
                      <th>구분</th>
                      <th className="num">금액</th>
                      <th>사람</th>
                      <th>분류</th>
                      <th>결제수단</th>
                      <th>메모</th>
                    </tr>
                  </thead>
                  <tbody>
                    {good.slice(0, PREVIEW).map((l) => (
                      <tr key={l.line}>
                        <td>{l.line}</td>
                        <td>{dayLabel(l.row!.day)}</td>
                        <td>{l.row!.kind === 'income' ? '수입' : '지출'}</td>
                        <td className="num">{won(l.row!.amount)}</td>
                        <td>{l.row!.person}</td>
                        <td>{l.row!.category}</td>
                        <td>{l.row!.payment}</td>
                        <td>{l.row!.memo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {good.length > PREVIEW && <p className="muted small">… 외 {good.length - PREVIEW}줄</p>}
              </div>
            )}
            <button className="primary" type="button" disabled={busy || good.length === 0} onClick={() => void send()}>
              {busy ? '보내는 중…' : `${good.length}줄 가져오기`}
            </button>
          </>
        )}
        {progress && busy && (
          <p role="status" className="muted">
            {progress.sent}줄 보냄
          </p>
        )}
        <ErrorText text={error} />
        {result && (
          <p className="ok" role="status">
            {result}
          </p>
        )}
      </section>
    </div>
  )
}
